import { useState, useCallback, useEffect } from 'react';
import { Loader2, RefreshCw, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function PairRow({ pairKey, data }) {
  const [expanded, setExpanded] = useState(false);
  const [sourceA, sourceB] = pairKey.split('|');

  return (
    <div className="border rounded" data-testid={`duplicate-pair-${pairKey}`}>
      <button
        type="button"
        className="w-full flex items-center justify-between p-2 text-xs"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="flex items-center gap-2">
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          <strong>{sourceA}</strong> &times; <strong>{sourceB}</strong>
        </span>
        <Badge variant="outline" className="text-[10px]">{fmtInt(data.count)} overlapping</Badge>
      </button>
      {expanded && (
        <div className="p-2 pt-0 space-y-1">
          {data.samples.map((s, i) => (
            <div key={i} className="text-[11px] text-muted-foreground border-t pt-1">
              #{s.normalized_invoice_number || '(no number)'} -- {s.amount} -- entity {s.entity_id || '(none)'}
              <br />
              {sourceA}: {(s[sourceA] || []).join(', ')} | {sourceB}: {(s[sourceB] || []).join(', ')}
            </div>
          ))}
          {data.count > data.samples.length && (
            <div className="text-[11px] text-muted-foreground italic">
              +{fmtInt(data.count - data.samples.length)} more not shown (sample capped)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Read-only cross-source duplication census (Commit 2): pairwise overlap
// of (normalized invoice_number, amount, entity_id) across every source
// pair present in db.invoices. Report only -- no merge/exclusion action
// here, that is a separate decision once the counts are known.
export function InvoiceDuplicateCensusPanel() {
  const [census, setCensus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runCensus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await diagnosticsAPI.invoiceDuplicateCensus();
      setCensus(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { runCensus(); }, [runCensus]);

  const pairs = census?.pairs || {};
  const pairKeys = Object.keys(pairs).sort((a, b) => pairs[b].count - pairs[a].count);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Copy className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Invoice Duplicate Census</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Read-only, cross-source pairwise overlap: docs sharing a normalized invoice
          number, amount, and entity across two different sources (e.g. bros x xero).
          Report only -- no merge or exclusion here, that's a separate decision once
          the counts are known.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Button data-testid="invoice-duplicate-census-refresh" onClick={runCensus} disabled={loading} size="sm" variant="outline">
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Refresh
          </Button>
          {census?.generated_at && (
            <span className="text-xs text-muted-foreground">
              as of {new Date(census.generated_at).toLocaleString('en-GB')}
            </span>
          )}
        </div>

        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}

        {census && (
          <div className="text-xs space-y-2" data-testid="invoice-duplicate-census-summary">
            <div>
              Total docs scanned: <strong>{fmtInt(census.total_docs_scanned)}</strong>
            </div>

            {pairKeys.length === 0 && (
              <div className="text-muted-foreground">No cross-source overlaps found.</div>
            )}

            <div className="space-y-1">
              {pairKeys.map((pairKey) => (
                <PairRow key={pairKey} pairKey={pairKey} data={pairs[pairKey]} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
